/**
 * LatestTradesCard Component
 *
 * Displays recent AI trades in a compact horizontal scrolling grid with AI model badges.
 * Symbols are deduplicated - if multiple AI models picked the same stock, all badges are shown.
 * Tapping a card opens a bottom sheet with rationales from all AI models that picked it.
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, spacing, fontStyles } from '../../styles';
import StockLogo from '../StockLogo';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import { PortfolioSelectionModal } from '../PortfolioSelectionModal';
import { useWatchlistActions } from '../../hooks/useWatchlistActions';
import {
  InvestingArenaTrade,
  AIModelId,
  getInvestingArenaActivity,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
} from '../../apis/investingArena';
import * as stores from '../../stores';
import { FREE_TRADES_LIMIT } from '../../constants/freemium';

export type LatestTradesCardProps = Record<string, never>;

// Grouped trades by symbol
interface GroupedPick {
  symbol: string;
  trades: InvestingArenaTrade[];
  modelIds: AIModelId[];
}

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// Skeleton loader for the grid
export const LatestTradesCardSkeleton: FC = () => {
  return (
    <div
      css={css`
        display: flex;
        gap: ${spacing.centi};
        overflow-x: auto;
        padding: ${spacing.micro} 0;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
      `}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          css={css`
            flex: 0 0 auto;
            width: 64px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${spacing.micro};
          `}
        >
          <div
            css={css`
              width: 48px;
              height: 48px;
              border-radius: 20%;
              background: ${colors.cloud};
              ${skeletonPulse}

              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
          <div
            css={css`
              width: 40px;
              height: 12px;
              border-radius: 4px;
              background: ${colors.cloud};
              ${skeletonPulse}

              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        </div>
      ))}
    </div>
  );
};

export const LatestTradesCard: FC<LatestTradesCardProps> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [trades, setTrades] = useState<InvestingArenaTrade[]>([]);
  const [selectedPick, setSelectedPick] = useState<GroupedPick | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const hasActiveSubscription = !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Watchlist actions from shared hook
  const {
    isWatchlistModalOpen,
    closeWatchlistModal,
    plaidPortfolioIds,
    isInWatchlist,
    handleAddToWatchlist,
    handlePortfolioSubmit,
  } = useWatchlistActions();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getInvestingArenaActivity(undefined, 12);
        setTrades(data);
      } catch (error) {
        console.error('[LatestTradesCard] Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group trades by symbol to deduplicate
  const groupedPicks = useMemo((): GroupedPick[] => {
    const symbolMap = new Map<string, InvestingArenaTrade[]>();

    trades.forEach((trade) => {
      const existing = symbolMap.get(trade.symbol) || [];
      // Only add if this model hasn't already picked this symbol
      if (!existing.some((t) => t.portfolio_id === trade.portfolio_id)) {
        symbolMap.set(trade.symbol, [...existing, trade]);
      }
    });

    // Convert to array and maintain order of first appearance
    const seen = new Set<string>();
    const result: GroupedPick[] = [];

    trades.forEach((trade) => {
      if (!seen.has(trade.symbol)) {
        seen.add(trade.symbol);
        const tradesForSymbol = symbolMap.get(trade.symbol) || [];
        result.push({
          symbol: trade.symbol,
          trades: tradesForSymbol,
          modelIds: tradesForSymbol.map((t) => t.portfolio_id),
        });
      }
    });

    return result;
  }, [trades]);

  const handleCardClick = (pick: GroupedPick) => {
    setSelectedPick(pick);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    closeWatchlistModal(); // Also close portfolio modal if open
  };

  const handleViewStock = () => {
    if (selectedPick) {
      setIsOpen(false);
      closeWatchlistModal(); // Also close portfolio modal if open
      history.push(`/symbol/${selectedPick.symbol}`);
    }
  };

  const onAddToWatchlist = () => {
    if (selectedPick) {
      handleAddToWatchlist(selectedPick.symbol);
    }
  };

  const onPortfolioSubmit = (selectedIds: string[]) => {
    if (selectedPick) {
      handlePortfolioSubmit(selectedPick.symbol, selectedIds);
    }
  };

  if (isLoading) {
    return <LatestTradesCardSkeleton />;
  }

  if (groupedPicks.length === 0) {
    return null;
  }

  // Render a single trade card
  const renderTradeCard = (pick: GroupedPick, isLocked: boolean) => {
    const primaryAction = pick.trades[0].action;
    const actionColor = primaryAction === 'BUY' ? colors.leaf : colors.berry;

    const handleClick = () => {
      if (isLocked) {
        stores.showPaymentModalByPreference('ideas_page_trades');
      } else {
        handleCardClick(pick);
      }
    };

    return (
      <button
        key={pick.symbol}
        onClick={handleClick}
        css={css`
          flex: 0 0 auto;
          width: 64px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${spacing.micro};
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;

          &:hover {
            opacity: 0.8;
          }
        `}
        aria-label={
          isLocked
            ? 'Unlock trades with Pro'
            : `${primaryAction} ${pick.symbol} by ${pick.modelIds.map((id) => AI_MODEL_NAMES[id]).join(' and ')}`
        }
      >
        <div
          css={css`
            position: relative;
            width: 48px;
            height: 48px;
          `}
        >
          <StockLogo symbol={pick.symbol} size="large" />

          {/* Gray overlay for locked cards */}
          {isLocked && (
            <div
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: #cbcac9;
                border-radius: 12px;
                opacity: 0.7;
              `}
            />
          )}

          {/* AI badges */}
          {pick.modelIds.map((modelId, idx) => {
            const modelIcon = AI_MODEL_ICONS[modelId];
            const rightOffset = -4 + idx * 14;

            return (
              <div
                key={modelId}
                css={css`
                  position: absolute;
                  bottom: -4px;
                  right: ${rightOffset}px;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: white;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 1px solid ${colors.cloud};
                  z-index: ${10 - idx};
                  color: ${AI_MODEL_COLORS[modelId]};

                  html[data-theme='dark'] & {
                    background: #2a2a2a;
                    border-color: ${colors.tungsten};
                  }
                `}
              >
                {modelIcon && (
                  <img
                    src={modelIcon}
                    alt=""
                    css={css`
                      width: 12px;
                      height: 12px;
                    `}
                  />
                )}
              </div>
            );
          })}
        </div>

        <span
          css={css`
            ${fontStyles.flea}
            color: var(--text-color);
            font-weight: 500;
            ${isLocked && 'opacity: 0.4;'}
          `}
        >
          {pick.symbol}
        </span>

        {isLocked ? (
          <span
            css={css`
              ${fontStyles.fairyfly}
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              padding: 2px 6px;
              border-radius: 4px;
              text-transform: uppercase;
              font-weight: 600;
              letter-spacing: 0.5px;
            `}
          >
            Pro
          </span>
        ) : (
          <span
            css={css`
              ${fontStyles.fairyfly}
              color: ${actionColor};
              text-transform: uppercase;
              font-weight: 600;
              letter-spacing: 0.5px;
            `}
          >
            {primaryAction}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Horizontal scrolling grid */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          overflow-x: auto;
          padding: ${spacing.micro} 0;
          margin-right: -${spacing.deci};
          padding-right: ${spacing.deci};
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        `}
      >
        {/* Render all cards - locked cards get dimmed with PRO badge */}
        {groupedPicks.map((pick, index) => {
          const isLocked = !hasActiveSubscription && index >= FREE_TRADES_LIMIT;
          return renderTradeCard(pick, isLocked);
        })}
      </div>

      {/* Bottom sheet for rationale */}
      <BottomSheet isOpen={isOpen} onClose={handleClose}>
        {selectedPick && (
          <>
            {/* Custom header with stock info + close button */}
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-bottom: ${spacing.deci};
                border-bottom: 1px solid ${colors.fog};
                margin-bottom: ${spacing.deci};

                html[data-theme='dark'] & {
                  border-color: ${colors.tungsten};
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.centi};
                `}
              >
                <StockLogo symbol={selectedPick.symbol} size="large" />
                <div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.milli};
                    `}
                  >
                    <span
                      css={css`
                        ${fontStyles.lion}
                        color: var(--text-color);
                      `}
                    >
                      {selectedPick.symbol}
                    </span>
                    <span
                      css={css`
                        ${fontStyles.flea}
                        color: ${selectedPick.trades[0].action === 'BUY' ? colors.leaf : colors.berry};
                        text-transform: uppercase;
                      `}
                    >
                      {selectedPick.trades[0].action}
                    </span>
                  </div>
                  <div
                    css={css`
                      ${fontStyles.mouse}
                      color: ${colors.silver};
                      display: flex;
                      align-items: center;
                      gap: ${spacing.micro};
                    `}
                  >
                    {selectedPick.modelIds.length > 1 ? (
                      <span>
                        Picked by{' '}
                        {selectedPick.modelIds.map((id, idx) => (
                          <span key={id}>
                            <span
                              css={css`
                                color: ${AI_MODEL_COLORS[id]};
                              `}
                            >
                              {AI_MODEL_NAMES[id]}
                            </span>
                            {idx < selectedPick.modelIds.length - 1 && ' & '}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <>
                        <span
                          css={css`
                            color: ${AI_MODEL_COLORS[selectedPick.trades[0].portfolio_id]};
                          `}
                        >
                          {AI_MODEL_NAMES[selectedPick.trades[0].portfolio_id]}
                        </span>
                        <span>·</span>
                        <span>${selectedPick.trades[0].fill_price.toFixed(2)}</span>
                        <span>·</span>
                        <span>{selectedPick.trades[0].relative_time}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                css={css`
                  background: none;
                  border: none;
                  padding: 8px;
                  cursor: pointer;
                  color: ${colors.tungsten};
                  font-size: 24px;
                  line-height: 1;

                  &:hover {
                    color: ${colors.liquorice};
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                    &:hover {
                      color: ${colors.sugar};
                    }
                  }
                `}
                onClick={handleClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Show rationale from each AI model */}
            {selectedPick.trades.map((trade, idx) => (
              <div
                key={trade.order_id || idx}
                css={css`
                  margin-bottom: ${spacing.deci};
                  ${idx > 0 &&
                  css`
                    padding-top: ${spacing.deci};
                    border-top: 1px solid ${colors.fog};
                    html[data-theme='dark'] & {
                      border-color: ${colors.tungsten};
                    }
                  `}
                `}
              >
                {/* Model header for multi-model picks */}
                {selectedPick.trades.length > 1 && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.milli};
                      margin-bottom: ${spacing.centi};
                    `}
                  >
                    <div
                      css={css`
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 1px solid ${colors.cloud};
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        html[data-theme='dark'] & {
                          background: #2a2a2a;
                          border-color: ${colors.tungsten};
                        }
                      `}
                    >
                      <img
                        src={AI_MODEL_ICONS[trade.portfolio_id]}
                        alt=""
                        css={css`
                          width: 14px;
                          height: 14px;
                        `}
                      />
                    </div>
                    <span
                      css={css`
                        ${fontStyles.dog}
                        color: ${AI_MODEL_COLORS[trade.portfolio_id]};
                        font-weight: 500;
                      `}
                    >
                      {AI_MODEL_NAMES[trade.portfolio_id]}
                    </span>
                    <span
                      css={css`
                        ${fontStyles.mouse}
                        color: ${colors.silver};
                      `}
                    >
                      · ${trade.fill_price.toFixed(2)} · {trade.relative_time}
                    </span>
                  </div>
                )}

                {/* Rationale */}
                <div
                  css={css`
                    margin-bottom: ${spacing.centi};
                  `}
                >
                  <h4
                    css={css`
                      ${fontStyles.mouse}
                      color: ${colors.silver};
                      margin: 0 0 ${spacing.micro} 0;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    `}
                  >
                    {t('trades.rationale')}
                  </h4>
                  <p
                    css={css`
                      ${fontStyles.cat}
                      color: var(--text-color);
                      margin: 0;
                      line-height: 1.5;
                    `}
                  >
                    {trade.rationale}
                  </p>
                </div>

                {/* Catalysts */}
                {trade.catalysts && trade.catalysts.length > 0 && (
                  <div
                    css={css`
                      margin-bottom: ${spacing.centi};
                    `}
                  >
                    <h4
                      css={css`
                        ${fontStyles.mouse}
                        color: ${colors.silver};
                        margin: 0 0 ${spacing.micro} 0;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                      `}
                    >
                      {t('trades.catalysts')}
                    </h4>
                    <ul
                      css={css`
                        ${fontStyles.cat}
                        color: var(--text-color);
                        margin: 0;
                        padding-left: ${spacing.deci};
                      `}
                    >
                      {trade.catalysts.map((catalyst, i) => (
                        <li key={i}>{catalyst}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Risks */}
                {trade.risks && trade.risks.length > 0 && (
                  <div>
                    <h4
                      css={css`
                        ${fontStyles.mouse}
                        color: ${colors.silver};
                        margin: 0 0 ${spacing.micro} 0;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                      `}
                    >
                      {t('trades.risks')}
                    </h4>
                    <ul
                      css={css`
                        ${fontStyles.cat}
                        color: var(--text-color);
                        margin: 0;
                        padding-left: ${spacing.deci};
                      `}
                    >
                      {trade.risks.map((risk, i) => (
                        <li key={i}>{risk}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}

            {/* Stacked action buttons */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.deci};
                margin-top: ${spacing.regular};
              `}
            >
              <Button variant="primary" block onClick={handleViewStock}>
                {t('trades.viewSymbol', { symbol: selectedPick.symbol })}
              </Button>
              <Button variant="secondary" block onClick={onAddToWatchlist}>
                {isInWatchlist(selectedPick.symbol) ? t('trades.inWatchlist') : t('trades.addToWatchlist')}
              </Button>
            </div>
          </>
        )}
      </BottomSheet>

      {/* Portfolio selection modal for multi-portfolio users */}
      <PortfolioSelectionModal
        isOpen={isWatchlistModalOpen}
        onClose={closeWatchlistModal}
        onSubmit={onPortfolioSubmit}
        title={t('trades.addToPortfolio')}
        symbol={selectedPick?.symbol}
        excludePortfolioIds={plaidPortfolioIds}
      />
    </>
  );
};

export default LatestTradesCard;
